import { useState, useRef, useEffect } from "react";
import {
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Analytics as AnalyticsIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Event as EventIcon,
  LocationCity as CityIcon,
  Groups as GroupsIcon,
  Computer as OnlineIcon,
  Business as OfflineIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Publish as PublishIcon,
  Email as EmailIcon,
  NotificationsActive as NotificationsActiveIcon,
  HowToReg as HowToRegIcon,
} from "@mui/icons-material";
import QRCode from "qrcode";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parseISO, formatISO } from "date-fns";
import {
  convertDateTimeLocalToCityTime,
  convertCityTimeToDateTimeLocal,
  formatDateInCityTimezone,
} from "../../../shared/lib/timezone";
import EventStatsModal from "./event-stats-modal";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  FILE_SIZE_LIMIT_BYTES,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";
import {
  EventListItem,
  CreateEventRequest,
  UpdateEventRequest,
  ResidentCaliber,
  UserRole,
  getResidentDisplayName,
  getRoleDisplayName,
  useGetEventsQuery,
  useGetEventQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  usePublishEventMutation,
  useGetEventCategoriesQuery,
  useGetEventSpeakersQuery,
  useUploadEventThumbnailMutation,
  useUploadEventLargeImageMutation,
  useGetEventQRCodesQuery,
  useCreateEventQRCodeMutation,
  useDeleteEventQRCodeMutation,
  useGetEventPricesQuery,
  useCreateEventPriceMutation,
  useUpdateEventPriceMutation,
  useDeleteEventPriceMutation,
  EVENT_PRICE_TYPE,
  CURRENCIES,
  getEventPriceTypeDisplayName,
  type EventPrice,
  type CreateEventPriceRequest,
  type UpdateEventPriceRequest,
  type EventPriceTypeId,
  useSendEventPreviewEmailMutation,
  useSendEventPreviewPushMutation,
} from "../../../entities/admin-events";
import { useGetCitiesQuery } from "../../../entities/admin-city";
import { useGetClubsQuery } from "../../../entities/admin-clubs";
import { useAppSelector } from "../../../app/store";
import { selectMe } from "../../../entities/me";
import { EventCategoriesManagement } from "./event-categories-management";
import { EventSpeakersManagement } from "./event-speakers-management";
import { EventContentManagement } from "./event-content-management";
import { EventApplicationsManagement } from "./event-applications-management";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";

/** Вкладки диалога редактирования мероприятия */
const EVENT_DIALOG_TAB = {
  MAIN: 0,
  QR_CODES: 1,
  CONTENT: 2,
  PRICES: 3,
  APPLICATIONS: 4,
} as const;

type EventDialogTab = (typeof EVENT_DIALOG_TAB)[keyof typeof EVENT_DIALOG_TAB];

export const EventsList = () => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedCities, setSelectedCities] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedClubs, setSelectedClubs] = useState<number[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<
    "startat" | "format" | "city" | "applicationCount" | "participantCount"
  >("startat");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  // "" — без фильтра, иначе фильтруем по признаку сформированного состава
  const [participantsFormedFilter, setParticipantsFormedFilter] = useState<
    "" | "true" | "false"
  >("");

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventListItem | null>(
    null,
  );

  const [createFormState, setCreateFormState] = useState<
    Partial<CreateEventRequest>
  >({
    calibers: [] as ResidentCaliber[],
    roles: [] as UserRole[],
    speakers: [],
  });

  const [updateFormState, setUpdateFormState] = useState<
    Partial<UpdateEventRequest>
  >({
    calibers: [] as ResidentCaliber[],
    roles: [] as UserRole[],
    speakers: [],
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [largeImageFile, setLargeImageFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [largeImagePreview, setLargeImagePreview] = useState<string | null>(
    null,
  );

  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const largeImageInputRef = useRef<HTMLInputElement>(null);

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  // Активная вкладка диалога редактирования
  const [dialogTab, setDialogTab] = useState<EventDialogTab>(
    EVENT_DIALOG_TAB.MAIN,
  );

  // Состояние для работы с QR-кодами
  const [qrCodeExpiresAt, setQrCodeExpiresAt] = useState<Date | null>(null);

  // Состояние для работы с ценами
  const [newPriceForm, setNewPriceForm] = useState<
    Partial<CreateEventPriceRequest>
  >({
    typeId: EVENT_PRICE_TYPE.NON_RESIDENT,
    currencyId: CURRENCIES[0].id,
    value: undefined,
    startAt: "",
  });
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editPriceForm, setEditPriceForm] = useState<
    Partial<CreateEventPriceRequest>
  >({});

  // Состояние для статистики
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [selectedEventForStats, setSelectedEventForStats] =
    useState<EventListItem | null>(null);

  const {
    data: eventsData,
    isLoading: isEventsLoading,
    refetch: refetchEvents,
  } = useGetEventsQuery({
    search: searchValue,
    from: startDate ? formatISO(startDate) : undefined,
    to: endDate ? formatISO(endDate) : undefined,
    cities: selectedCities.length ? selectedCities : undefined,
    categories: selectedCategories.length ? selectedCategories : undefined,
    clubs: selectedClubs.length ? selectedClubs : undefined,
    formats: selectedFormats.length
      ? (selectedFormats as ("online" | "offline")[])
      : undefined,
    participantsFormed:
      participantsFormedFilter === "" ? undefined : participantsFormedFilter === "true",
    sort: sortBy,
    order: sortOrder,
    pageToken,
    limit,
  });

  useEffect(() => {
    setPage(0);
    setPageToken(undefined);
  }, [
    searchValue,
    startDate,
    endDate,
    selectedCities,
    selectedCategories,
    selectedClubs,
    selectedFormats,
    participantsFormedFilter,
    sortBy,
    sortOrder,
  ]);

  const {
    data: eventData,
    isLoading: isEventLoading,
    refetch: refetchEvent,
  } = useGetEventQuery(selectedEventId || 0, { skip: !selectedEventId });

  useEffect(() => {
    if (selectedEventId && isUpdateDialogOpen) {
      console.log("refetchEvent");
      refetchEvent();
    }
  }, [selectedEventId, isUpdateDialogOpen, refetchEvent]);

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetEventCategoriesQuery();
  const { data: speakersData, isLoading: isSpeakersLoading } =
    useGetEventSpeakersQuery();
  const { data: citiesData, isLoading: isCitiesLoading } = useGetCitiesQuery();
  const { data: clubsData } = useGetClubsQuery();

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [publishEvent, { isLoading: isPublishing }] = usePublishEventMutation();
  const [uploadThumbnail] = useUploadEventThumbnailMutation();
  const [uploadLargeImage] = useUploadEventLargeImageMutation();

  const { data: eventQRCodesData, refetch: refetchQRCodes } =
    useGetEventQRCodesQuery(selectedEventId || 0, {
      skip: !selectedEventId || dialogTab !== EVENT_DIALOG_TAB.QR_CODES,
    });

  const [createQRCode, { isLoading: isCreatingQRCode }] =
    useCreateEventQRCodeMutation();
  const [deleteQRCode, { isLoading: isDeletingQRCode }] =
    useDeleteEventQRCodeMutation();

  const { data: eventPricesData, refetch: refetchPrices } =
    useGetEventPricesQuery(selectedEventId || 0, {
      skip: !selectedEventId || dialogTab !== EVENT_DIALOG_TAB.PRICES,
    });
  const [createEventPrice, { isLoading: isCreatingPrice }] =
    useCreateEventPriceMutation();
  const [updateEventPrice, { isLoading: isUpdatingPrice }] =
    useUpdateEventPriceMutation();
  const [deleteEventPrice, { isLoading: isDeletingPrice }] =
    useDeleteEventPriceMutation();

  const me = useAppSelector(selectMe);
  const [sendEventPreviewEmail, { isLoading: isSendingEventPreviewEmail }] =
    useSendEventPreviewEmailMutation();
  const [sendEventPreviewPush, { isLoading: isSendingEventPreviewPush }] =
    useSendEventPreviewPushMutation();
  const [previewSnackbar, setPreviewSnackbar] = useState<{
    open: boolean;
    message: React.ReactNode;
  }>({ open: false, message: "" });

  useEffect(() => {
    if (eventData?.result && eventData.event) {
      const event = eventData.event;
      const cityName = event.city.name;

      setUpdateFormState({
        title: event.title,
        description: event.description,
        categoryId: event.category.id,
        format: event.format,
        cityId: event.city.id,
        address: event.address,
        addressLocationUrl: event.addressLocationUrl || "",
        broadcastUrl: event.broadcastUrl || "",
        participantMax: event.participantMax,
        registrationStartAt: event.registrationStartAt
          ? convertCityTimeToDateTimeLocal(event.registrationStartAt, cityName)
          : "",
        registrationEndAt: event.registrationEndAt
          ? convertCityTimeToDateTimeLocal(event.registrationEndAt, cityName)
          : "",
        startAt: event.startAt
          ? convertCityTimeToDateTimeLocal(event.startAt, cityName)
          : "",
        endAt: event.endAt
          ? convertCityTimeToDateTimeLocal(event.endAt, cityName)
          : "",
        calibers: event.calibers as ResidentCaliber[],
        roles: event.roles as UserRole[],
        speakers: event.speakers.map((speaker) => speaker.id),
        clubId: event.club?.id ?? null,
        participantsFormed: event.participantsFormed ?? false,
      });
    }
  }, [eventData?.result, eventData?.event, isUpdateDialogOpen]);

  const handleOpenCreateDialog = () => {
    setCreateFormState({
      calibers: [] as ResidentCaliber[],
      roles: [] as UserRole[],
      speakers: [],
    });
    handleClearThumbnail();
    handleClearLargeImage();
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    handleClearThumbnail();
    handleClearLargeImage();
  };

  const handleOpenUpdateDialog = (event: EventListItem) => {
    setSelectedEvent(event);
    setSelectedEventId(event.id);

    setUpdateFormState({
      title: event.title,
      categoryId: event.category.id,
      format: event.format,
      cityId: event.city.id,
      address: event.address,
      calibers: event.calibers as ResidentCaliber[],
      roles: event.roles as UserRole[],
      participantsFormed: event.participantsFormed ?? false,
    });

    handleClearThumbnail();
    handleClearLargeImage();
    setIsUpdateDialogOpen(true);
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    setSelectedEvent(null);
    setSelectedEventId(null);
    handleClearThumbnail();
    handleClearLargeImage();
    setDialogTab(EVENT_DIALOG_TAB.MAIN);
    setEditingPriceId(null);
  };

  const handleOpenDeleteDialog = (event: EventListItem) => {
    setSelectedEvent(event);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleThumbnailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
      if (sizeError) {
        alert(sizeError);
        if (thumbnailInputRef.current) {
          thumbnailInputRef.current.value = "";
        }
        return;
      }

      setThumbnailFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLargeImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
      if (sizeError) {
        alert(sizeError);
        if (largeImageInputRef.current) {
          largeImageInputRef.current.value = "";
        }
        return;
      }

      setLargeImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLargeImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }
  };

  const handleClearLargeImage = () => {
    setLargeImageFile(null);
    setLargeImagePreview(null);
    if (largeImageInputRef.current) {
      largeImageInputRef.current.value = "";
    }
  };

  const uploadEventImages = async (eventId: number) => {
    try {
      if (thumbnailFile) {
        const formData = new FormData();
        formData.append("file", thumbnailFile);
        await uploadThumbnail({ id: eventId, file: formData }).unwrap();
      }

      if (largeImageFile) {
        const formData = new FormData();
        formData.append("file", largeImageFile);
        await uploadLargeImage({ id: eventId, file: formData }).unwrap();
      }

      return true;
    } catch (error) {
      console.error("Failed to upload images:", error);
      return false;
    }
  };

  const handleCreateChange = (
    field: keyof CreateEventRequest,
    value: unknown,
  ) => {
    setCreateFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateChange = (
    field: keyof UpdateEventRequest,
    value: unknown,
  ) => {
    setUpdateFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (
    event: SelectChangeEvent<number[]>,
    setState: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    const value = event.target.value as unknown as number[];
    setState(value);
  };

  const handleFormatChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as unknown as string[];
    setSelectedFormats(value);
  };

  const handleCalibersChange = (
    event: SelectChangeEvent<string[]>,
    isCreate: boolean = false,
  ) => {
    const value = event.target.value as unknown as ResidentCaliber[];
    if (isCreate) {
      handleCreateChange("calibers", value);
    } else {
      handleUpdateChange("calibers", value);
    }
  };

  const handleRolesChange = (
    event: SelectChangeEvent<string[]>,
    isCreate: boolean = false,
  ) => {
    const value = event.target.value as unknown as UserRole[];

    // Если выбрана только роль "Пользователь", очищаем calibers
    const onlyUserRole = value.length === 1 && value[0] === UserRole.USER;

    if (isCreate) {
      handleCreateChange("roles", value);
      if (onlyUserRole) {
        handleCreateChange("calibers", []);
      }
    } else {
      handleUpdateChange("roles", value);
      if (onlyUserRole) {
        handleUpdateChange("calibers", []);
      }
    }
  };

  const handleCreate = async () => {
    try {
      if (
        !createFormState.title ||
        !createFormState.description ||
        !createFormState.cityId ||
        !createFormState.address ||
        !createFormState.participantMax ||
        !createFormState.format ||
        !createFormState.categoryId ||
        !createFormState.registrationStartAt ||
        !createFormState.registrationEndAt ||
        !createFormState.startAt ||
        !createFormState.endAt
      ) {
        alert("Пожалуйста, заполните все обязательные поля");
        return;
      }

      // Проверка: должна быть хотя бы одна роль
      if (!createFormState.roles || createFormState.roles.length === 0) {
        alert("Необходимо выбрать хотя бы одну роль");
        return;
      }

      // Проверка: если указана роль "Резидент", должна быть хотя бы одна категория
      const hasResidentRole = createFormState.roles.includes(UserRole.RESIDENT);
      const onlyUserRole =
        createFormState.roles.length === 1 &&
        createFormState.roles[0] === UserRole.USER;

      if (
        hasResidentRole &&
        !onlyUserRole &&
        (!createFormState.calibers || createFormState.calibers.length === 0)
      ) {
        alert(
          'Если выбрана роль "Резидент", необходимо выбрать хотя бы одну категорию резидента',
        );
        return;
      }

      // Получаем название города для корректной обработки часового пояса
      const selectedCity = citiesData?.cities.find(
        (city) => city.id === createFormState.cityId,
      );
      const cityName = selectedCity?.name || "";

      const eventData: CreateEventRequest = {
        title: createFormState.title || "",
        description: createFormState.description || "",
        cityId: createFormState.cityId || 0,
        address: createFormState.address || "",
        addressLocationUrl: createFormState.addressLocationUrl || "",
        broadcastUrl: createFormState.broadcastUrl || "",
        participantMax: createFormState.participantMax || 0,
        registrationStartAt: createFormState.registrationStartAt
          ? convertDateTimeLocalToCityTime(
              createFormState.registrationStartAt,
              cityName,
            )
          : "",
        registrationEndAt: createFormState.registrationEndAt
          ? convertDateTimeLocalToCityTime(
              createFormState.registrationEndAt,
              cityName,
            )
          : "",
        format: createFormState.format as "online" | "offline",
        startAt: createFormState.startAt
          ? convertDateTimeLocalToCityTime(createFormState.startAt, cityName)
          : "",
        endAt: createFormState.endAt
          ? convertDateTimeLocalToCityTime(createFormState.endAt, cityName)
          : "",
        calibers: createFormState.calibers || [],
        roles: createFormState.roles || [],
        categoryId: createFormState.categoryId || 0,
        speakers: createFormState.speakers || [],
        isPublicContent: false,
        clubId: createFormState.clubId ?? null,
        participantsFormed: createFormState.participantsFormed ?? false,
      };

      const result = await createEvent(eventData).unwrap();

      if (result.result && result.event.id) {
        // Upload images if provided
        if (thumbnailFile || largeImageFile) {
          const uploadResult = await uploadEventImages(result.event.id);
          if (!uploadResult) {
            alert(
              "Мероприятие создано, но возникли проблемы с загрузкой изображений",
            );
          }
        }

        alert("Мероприятие успешно создано");
        setIsCreateDialogOpen(false);
        handleClearThumbnail();
        handleClearLargeImage();
        refetchEvents();
      }
    } catch (error) {
      console.error("Failed to create event:", error);
      alert("Не удалось создать мероприятие");
    }
  };

  const handleUpdate = async () => {
    if (!selectedEvent) return;

    try {
      if (
        !updateFormState.title ||
        !updateFormState.description ||
        !updateFormState.cityId ||
        !updateFormState.address ||
        !updateFormState.participantMax ||
        !updateFormState.format ||
        !updateFormState.categoryId ||
        !updateFormState.registrationStartAt ||
        !updateFormState.registrationEndAt ||
        !updateFormState.startAt ||
        !updateFormState.endAt
      ) {
        alert("Пожалуйста, заполните все обязательные поля");
        return;
      }

      // Проверка: должна быть хотя бы одна роль
      if (!updateFormState.roles || updateFormState.roles.length === 0) {
        alert("Необходимо выбрать хотя бы одну роль");
        return;
      }

      // Проверка: если указана роль "Резидент", должна быть хотя бы одна категория
      const hasResidentRole = updateFormState.roles.includes(UserRole.RESIDENT);
      const onlyUserRole =
        updateFormState.roles.length === 1 &&
        updateFormState.roles[0] === UserRole.USER;

      if (
        hasResidentRole &&
        !onlyUserRole &&
        (!updateFormState.calibers || updateFormState.calibers.length === 0)
      ) {
        alert(
          'Если выбрана роль "Резидент", необходимо выбрать хотя бы одну категорию резидента',
        );
        return;
      }

      // Получаем название города для корректной обработки часового пояса
      const selectedCity = citiesData?.cities.find(
        (city) => city.id === updateFormState.cityId,
      );
      const cityName = selectedCity?.name || "";

      const eventData: UpdateEventRequest = {
        title: updateFormState.title || "",
        description: updateFormState.description || "",
        cityId: updateFormState.cityId || 0,
        address: updateFormState.address || "",
        addressLocationUrl: updateFormState.addressLocationUrl || "",
        broadcastUrl: updateFormState.broadcastUrl || "",
        participantMax: updateFormState.participantMax || 0,
        registrationStartAt: updateFormState.registrationStartAt
          ? convertDateTimeLocalToCityTime(
              updateFormState.registrationStartAt,
              cityName,
            )
          : "",
        registrationEndAt: updateFormState.registrationEndAt
          ? convertDateTimeLocalToCityTime(
              updateFormState.registrationEndAt,
              cityName,
            )
          : "",
        format: updateFormState.format as "online" | "offline",
        startAt: updateFormState.startAt
          ? convertDateTimeLocalToCityTime(updateFormState.startAt, cityName)
          : "",
        endAt: updateFormState.endAt
          ? convertDateTimeLocalToCityTime(updateFormState.endAt, cityName)
          : "",
        calibers: updateFormState.calibers || [],
        roles: updateFormState.roles || [],
        categoryId: updateFormState.categoryId || 0,
        speakers: updateFormState.speakers || [],
        isPublicContent: false,
        clubId: updateFormState.clubId ?? null,
        participantsFormed: updateFormState.participantsFormed ?? false,
      };

      const result = await updateEvent({
        id: selectedEvent.id,
        body: eventData,
      }).unwrap();

      if (result.result) {
        // Upload images if provided
        if (thumbnailFile || largeImageFile) {
          const uploadResult = await uploadEventImages(selectedEvent.id);
          if (!uploadResult) {
            alert(
              "Мероприятие обновлено, но возникли проблемы с загрузкой изображений",
            );
          }
        }

        alert("Мероприятие успешно обновлено");
        setIsUpdateDialogOpen(false);
        setSelectedEvent(null);
        handleClearThumbnail();
        handleClearLargeImage();
        refetchEvents();
      }
    } catch (error) {
      console.error("Failed to update event:", error);
      alert("Не удалось обновить мероприятие");
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      const result = await deleteEvent(selectedEvent.id).unwrap();
      if (result.result) {
        alert("Мероприятие успешно удалено");
        setIsDeleteDialogOpen(false);
        setSelectedEvent(null);
        refetchEvents();
      }
    } catch (error) {
      console.error("Failed to delete event:", error);
      alert("Не удалось удалить мероприятие");
    }
  };

  const handlePublish = async (eventId: number) => {
    try {
      const result = await publishEvent(eventId).unwrap();
      if (result.result) {
        alert("Мероприятие успешно опубликовано");
        refetchEvents();
        if (selectedEventId === eventId) {
          refetchEvent();
        }
      }
    } catch (error) {
      console.error("Failed to publish event:", error);
      alert("Не удалось опубликовать мероприятие");
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    const next = eventsData?.pageToken?.trim();
    if (next) {
      setPageToken(next);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
    setPageToken(undefined);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleSortChange = (
    field:
      | "startat"
      | "format"
      | "city"
      | "applicationCount"
      | "participantCount",
  ) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleCreateQRCode = async () => {
    if (!selectedEventId || !qrCodeExpiresAt) {
      alert("Пожалуйста, укажите дату истечения QR-кода");
      return;
    }

    try {
      await createQRCode({
        id: selectedEventId,
        body: {
          expiresAt: formatISO(qrCodeExpiresAt),
        },
      }).unwrap();
      refetchQRCodes();
      setQrCodeExpiresAt(null);
    } catch (error) {
      console.error("Failed to create QR code:", error);
      alert("Не удалось создать QR-код");
    }
  };

  const handleDeleteQRCode = async (code: string) => {
    if (!selectedEventId) return;

    try {
      await deleteQRCode({
        id: selectedEventId,
        code,
      }).unwrap();
      refetchQRCodes();
    } catch (error) {
      console.error("Failed to delete QR code:", error);
      alert("Не удалось удалить QR-код");
    }
  };

  const handleDownloadQRCode = async (universalLink: string, code: string) => {
    try {
      // Генерируем QR-код в виде data URL
      const qrCodeDataUrl = await QRCode.toDataURL(universalLink, {
        width: 512,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      // Создаем ссылку для скачивания
      const link = document.createElement("a");
      link.href = qrCodeDataUrl;
      link.download = `qr-code-${code}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to generate QR code:", error);
      alert("Не удалось сгенерировать QR-код");
    }
  };

  const handleOpenStatsModal = (event: EventListItem) => {
    setSelectedEventForStats(event);
    setStatsModalOpen(true);
  };

  const handleCloseStatsModal = () => {
    setStatsModalOpen(false);
    setSelectedEventForStats(null);
  };

  const handleSendEventPreviewEmail = async (eventId?: number) => {
    const id = eventId ?? selectedEventId;
    if (!id) return;
    try {
      await sendEventPreviewEmail(id).unwrap();
      setPreviewSnackbar({
        open: true,
        message: me?.email ? (
          <Typography component="span" display="block">
            📧 Email отправлен на ваш адрес: {me.email}
          </Typography>
        ) : (
          <Typography component="span" display="block">
            📧 Email отправлен на ваш адрес в профиле
          </Typography>
        ),
      });
    } catch (error) {
      console.error("Error sending event preview email:", error);
    }
  };

  const handleSendEventPreviewPush = async (eventId?: number) => {
    const id = eventId ?? selectedEventId;
    if (!id) return;
    try {
      await sendEventPreviewPush(id).unwrap();
      setPreviewSnackbar({
        open: true,
        message: (
          <Typography component="span" display="block">
            📱 Push-уведомление должно прийти на ваше мобильное устройство
          </Typography>
        ),
      });
    } catch (error) {
      console.error("Error sending event preview push:", error);
    }
  };

  const cityName = selectedEvent?.city?.name || "";

  const handleCreatePrice = async () => {
    if (
      !selectedEventId ||
      !newPriceForm.typeId ||
      !newPriceForm.currencyId ||
      newPriceForm.value == null ||
      !newPriceForm.startAt
    ) {
      alert("Заполните все поля цены");
      return;
    }
    try {
      const startAtCity = convertDateTimeLocalToCityTime(
        newPriceForm.startAt,
        cityName,
      );
      await createEventPrice({
        id: selectedEventId,
        body: {
          typeId: newPriceForm.typeId,
          currencyId: newPriceForm.currencyId,
          value: Number(newPriceForm.value),
          startAt: startAtCity,
        },
      }).unwrap();
      refetchPrices();
      setNewPriceForm({
        typeId: EVENT_PRICE_TYPE.NON_RESIDENT,
        currencyId: CURRENCIES[0].id,
        value: undefined,
        startAt: "",
      });
    } catch (error) {
      console.error("Failed to create price:", error);
      alert("Не удалось добавить цену");
    }
  };

  const handleStartEditPrice = (price: EventPrice) => {
    setEditingPriceId(price.id);
    // Найти валюту по unicode символу
    const currency = CURRENCIES.find(
      (c) => c.symbol === price.price.unicode || c.code === price.price.unicode
    );
    setEditPriceForm({
      typeId: price.typeId,
      currencyId: currency?.id ?? CURRENCIES[0].id,
      value: price.price.value,
      startAt: selectedEvent?.city?.name
        ? convertCityTimeToDateTimeLocal(price.startAt, selectedEvent.city.name)
        : price.startAt,
    });
  };

  const handleCancelEditPrice = () => {
    setEditingPriceId(null);
    setEditPriceForm({});
  };

  const handleUpdatePrice = async () => {
    if (!selectedEventId || editingPriceId == null) return;
    const body: UpdateEventPriceRequest = {};
    if (editPriceForm.typeId != null)
      body.typeId = editPriceForm.typeId as EventPriceTypeId;
    if (editPriceForm.currencyId != null)
      body.currencyId = editPriceForm.currencyId;
    if (editPriceForm.value != null) body.value = editPriceForm.value;
    if (editPriceForm.startAt)
      body.startAt = convertDateTimeLocalToCityTime(
        editPriceForm.startAt,
        cityName,
      );
    if (Object.keys(body).length === 0) {
      handleCancelEditPrice();
      return;
    }
    try {
      await updateEventPrice({
        id: selectedEventId,
        priceId: editingPriceId,
        body,
      }).unwrap();
      refetchPrices();
      handleCancelEditPrice();
    } catch (error) {
      console.error("Failed to update price:", error);
      alert("Не удалось обновить цену");
    }
  };

  const handleDeletePrice = async (priceId: number) => {
    if (!selectedEventId) return;
    try {
      await deleteEventPrice({ id: selectedEventId, priceId }).unwrap();
      refetchPrices();
      if (editingPriceId === priceId) handleCancelEditPrice();
    } catch (error) {
      console.error("Failed to delete price:", error);
      alert("Не удалось удалить цену");
    }
  };

  if (
    isEventsLoading ||
    isCategoriesLoading ||
    isSpeakersLoading ||
    isCitiesLoading
  ) {
    return <LoadingIndicator />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ py: 3, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon fontSize="large" color="primary" />
            <Typography fontWeight={600} fontSize={24}>
              Управление мероприятиями
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Добавить мероприятие
          </Button>
        </Box>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <ArrowUpIcon fontSize="small" />
                <ArrowDownIcon fontSize="small" />
                Нажмите на заголовки колонок для сортировки
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid sx={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Поиск по названию"
                  value={searchValue}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: <SearchIcon />,
                  }}
                />
              </Grid>
              <Grid sx={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Дата начала"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                />
              </Grid>
              <Grid sx={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Дата окончания"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                />
              </Grid>
              <Grid container spacing={2} sx={{ alignItems: "center" }}>
                <Grid sx={{ xs: 12, sm: 4, minWidth: 220 }}>
                  <FormControl fullWidth>
                    <InputLabel>Город</InputLabel>
                    <Select
                      multiple
                      value={selectedCities}
                      onChange={(e) => handleSelectChange(e, setSelectedCities)}
                      input={<OutlinedInput label="Город" />}
                    >
                      {citiesData?.cities.map((city) => (
                        <MenuItem key={city.id} value={city.id}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid sx={{ xs: 12, sm: 4, minWidth: 220 }}>
                  <FormControl fullWidth>
                    <InputLabel>Категория</InputLabel>
                    <Select
                      multiple
                      value={selectedCategories}
                      onChange={(e) =>
                        handleSelectChange(e, setSelectedCategories)
                      }
                      input={<OutlinedInput label="Категория" />}
                    >
                      {categoriesData?.categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid sx={{ xs: 12, sm: 4, minWidth: 220 }}>
                  <FormControl fullWidth>
                    <InputLabel>Клуб</InputLabel>
                    <Select
                      multiple
                      value={selectedClubs}
                      onChange={(e) => handleSelectChange(e, setSelectedClubs)}
                      input={<OutlinedInput label="Клуб" />}
                    >
                      {clubsData?.clubs.map((club) => (
                        <MenuItem key={club.id} value={club.id}>
                          {club.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid sx={{ xs: 12, sm: 4, minWidth: 220 }}>
                  <FormControl fullWidth>
                    <InputLabel>Формат</InputLabel>
                    <Select
                      multiple
                      value={selectedFormats}
                      onChange={handleFormatChange}
                      input={<OutlinedInput label="Формат" />}
                    >
                      <MenuItem value="online">Онлайн</MenuItem>
                      <MenuItem value="offline">Оффлайн</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid sx={{ xs: 12, sm: 4, minWidth: 220 }}>
                  <FormControl fullWidth>
                    <InputLabel>Состав участников</InputLabel>
                    <Select
                      value={participantsFormedFilter}
                      onChange={(e) =>
                        setParticipantsFormedFilter(
                          e.target.value as "" | "true" | "false",
                        )
                      }
                      input={<OutlinedInput label="Состав участников" />}
                    >
                      <MenuItem value="">Все</MenuItem>
                      <MenuItem value="true">Сформирован</MenuItem>
                      <MenuItem value="false">Не сформирован</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSortChange("city")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CityIcon fontSize="small" />
                    Город
                    {sortBy === "city" &&
                      (sortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <GroupsIcon fontSize="small" />
                    Клуб
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSortChange("format")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <EventIcon fontSize="small" />
                    Формат
                    {sortBy === "format" &&
                      (sortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSortChange("startat")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <EventIcon fontSize="small" />
                    Дата начала
                    {sortBy === "startat" &&
                      (sortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSortChange("applicationCount")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonAddIcon fontSize="small" />
                    Зарегистрировано
                    {sortBy === "applicationCount" &&
                      (sortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSortChange("participantCount")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PeopleIcon fontSize="small" />
                    Участники
                    {sortBy === "participantCount" &&
                      (sortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <HowToRegIcon fontSize="small" />
                    Состав
                  </Box>
                </TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isEventsLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : eventsData?.events && eventsData.events.length > 0 ? (
                eventsData.events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.id}</TableCell>
                    <TableCell>{event.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          event.status === "published"
                            ? "Опубликовано"
                            : "Черновик"
                        }
                        color={
                          event.status === "published" ? "success" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CityIcon fontSize="small" />
                        {event.city.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {event.club ? (
                        <Chip
                          avatar={
                            <Avatar src={event.club.logoUrl}>
                              {event.club.name?.[0]}
                            </Avatar>
                          }
                          label={event.club.name}
                          variant="outlined"
                          size="small"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {event.format === "online" ? (
                          <OnlineIcon fontSize="small" color="primary" />
                        ) : (
                          <OfflineIcon fontSize="small" color="secondary" />
                        )}
                        {event.format === "online" ? "Онлайн" : "Оффлайн"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatDateInCityTimezone(
                        event.startAt,
                        event.city.name,
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <PersonAddIcon fontSize="small" color="action" />
                        {event.applicationCount ?? 0}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <PeopleIcon fontSize="small" color="action" />
                        {event.participantCount}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {event.participantsFormed ? (
                        <Chip
                          label="Сформирован"
                          color="warning"
                          size="small"
                          title="Запись из приложения закрыта"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenUpdateDialog(event)}
                        >
                          <EditIcon />
                        </IconButton>
                        {event.status === "draft" && (
                          <IconButton
                            color="success"
                            onClick={() => handlePublish(event.id)}
                            disabled={isPublishing}
                            title="Опубликовать"
                          >
                            <PublishIcon />
                          </IconButton>
                        )}
                        <IconButton
                          color="info"
                          onClick={() => handleOpenStatsModal(event)}
                        >
                          <AnalyticsIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleOpenDeleteDialog(event)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={-1} // Неизвестное количество
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={() => ""}
          />
        </TableContainer>

        {/* Диалог создания мероприятия */}
        <Dialog
          open={isCreateDialogOpen}
          onClose={handleCloseCreateDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Создать мероприятие</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Название"
                required
                value={createFormState.title || ""}
                onChange={(e) => handleCreateChange("title", e.target.value)}
              />

              <TextField
                fullWidth
                label="Описание"
                required
                multiline
                rows={4}
                value={createFormState.description || ""}
                onChange={(e) =>
                  handleCreateChange("description", e.target.value)
                }
              />

              <FormControl fullWidth required>
                <InputLabel>Категория</InputLabel>
                <Select
                  value={createFormState.categoryId || ""}
                  onChange={(e) =>
                    handleCreateChange("categoryId", e.target.value)
                  }
                  label="Категория"
                >
                  {categoriesData?.categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Клуб</InputLabel>
                <Select
                  value={createFormState.clubId ?? ""}
                  onChange={(e: SelectChangeEvent<number | "">) =>
                    handleCreateChange(
                      "clubId",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  label="Клуб"
                >
                  <MenuItem value="">Не выбран</MenuItem>
                  {clubsData?.clubs.map((club) => (
                    <MenuItem key={club.id} value={club.id}>
                      {club.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Формат</InputLabel>
                <Select
                  value={createFormState.format || ""}
                  onChange={(e) => handleCreateChange("format", e.target.value)}
                  label="Формат"
                >
                  <MenuItem value="online">Онлайн</MenuItem>
                  <MenuItem value="offline">Оффлайн</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Город</InputLabel>
                <Select
                  value={createFormState.cityId || ""}
                  onChange={(e) => handleCreateChange("cityId", e.target.value)}
                  label="Город"
                >
                  {citiesData?.cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Адрес"
                required
                value={createFormState.address || ""}
                onChange={(e) => handleCreateChange("address", e.target.value)}
              />
              <TextField
                fullWidth
                label="Ссылка на карту"
                value={createFormState.addressLocationUrl || ""}
                onChange={(e) =>
                  handleCreateChange("addressLocationUrl", e.target.value)
                }
              />

              <TextField
                fullWidth
                label="URL трансляции"
                value={createFormState.broadcastUrl || ""}
                onChange={(e) =>
                  handleCreateChange("broadcastUrl", e.target.value)
                }
              />

              <TextField
                fullWidth
                label="Максимальное число участников"
                required
                type="number"
                inputProps={{ min: 1 }}
                value={createFormState.participantMax || ""}
                onChange={(e) =>
                  handleCreateChange("participantMax", parseInt(e.target.value))
                }
              />

              <Box sx={{ width: "100%" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(createFormState.participantsFormed)}
                      onChange={(e) =>
                        handleCreateChange(
                          "participantsFormed",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Состав участников сформирован"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Мероприятие останется в календаре, но записаться на него из приложения будет нельзя — пользователь увидит сообщение, что состав уже сформирован.
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Дата и время начала регистрации"
                type="datetime-local"
                required
                value={createFormState.registrationStartAt || ""}
                onChange={(e) =>
                  handleCreateChange("registrationStartAt", e.target.value)
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                label="Дата и время окончания регистрации"
                type="datetime-local"
                required
                value={createFormState.registrationEndAt || ""}
                onChange={(e) =>
                  handleCreateChange("registrationEndAt", e.target.value)
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                label="Дата и время начала мероприятия"
                type="datetime-local"
                required
                value={createFormState.startAt || ""}
                onChange={(e) => handleCreateChange("startAt", e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                label="Дата и время окончания мероприятия"
                type="datetime-local"
                required
                value={createFormState.endAt || ""}
                onChange={(e) => handleCreateChange("endAt", e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Спикеры</InputLabel>
                <Select
                  multiple
                  value={createFormState.speakers || []}
                  onChange={(e) =>
                    handleCreateChange(
                      "speakers",
                      e.target.value as unknown as number[],
                    )
                  }
                  input={<OutlinedInput label="Спикеры" />}
                >
                  {speakersData?.speakers?.map((speaker) => (
                    <MenuItem key={speaker.id} value={speaker.id}>
                      {`${speaker.lastName} ${speaker.firstName} ${
                        speaker.patronymic || ""
                      }`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Роли пользователей</InputLabel>
                <Select
                  multiple
                  value={createFormState.roles || []}
                  onChange={(e) => handleRolesChange(e, true)}
                  input={<OutlinedInput label="Роли пользователей" />}
                >
                  {Object.values(UserRole).map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                disabled={
                  createFormState.roles?.length === 1 &&
                  createFormState.roles[0] === UserRole.USER
                }
              >
                <InputLabel>Доступен резидентам</InputLabel>
                <Select
                  multiple
                  value={createFormState.calibers || []}
                  onChange={(e) => handleCalibersChange(e, true)}
                  input={<OutlinedInput label="Доступен резидентам" />}
                >
                  {Object.values(ResidentCaliber).map((resident) => (
                    <MenuItem key={resident} value={resident}>
                      {getResidentDisplayName(resident)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Thumbnail image upload */}
              <Box sx={{ width: "100%", mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Миниатюра мероприятия
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleThumbnailChange}
                  ref={thumbnailInputRef}
                  id="thumbnail-upload"
                />
                <label htmlFor="thumbnail-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Загрузить миниатюру
                  </Button>
                </label>
                <FileUploadInfo
                  type="image"
                  recommendedSize="400x300 пикселей"
                />
                {thumbnailPreview && (
                  <Box sx={{ mt: 2, position: "relative", maxWidth: "300px" }}>
                    <img
                      src={thumbnailPreview}
                      alt="Предпросмотр миниатюры"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "150px",
                        objectFit: "contain",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleClearThumbnail}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Large image upload */}
              <Box sx={{ width: "100%", mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Большое изображение мероприятия
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleLargeImageChange}
                  ref={largeImageInputRef}
                  id="large-image-upload"
                />
                <label htmlFor="large-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Загрузить большое изображение
                  </Button>
                </label>
                <FileUploadInfo
                  type="image"
                  recommendedSize="1920x1080 пикселей"
                />
                {largeImagePreview && (
                  <Box sx={{ mt: 2, position: "relative", maxWidth: "300px" }}>
                    <img
                      src={largeImagePreview}
                      alt="Предпросмотр большого изображения"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "150px",
                        objectFit: "contain",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleClearLargeImage}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Отмена</Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={isCreating}
            >
              Создать
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог редактирования мероприятия */}
        <Dialog
          open={isUpdateDialogOpen}
          onClose={handleCloseUpdateDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Редактировать мероприятие</DialogTitle>
          <DialogContent>
            {isEventLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <Typography>Загрузка данных о мероприятии...</Typography>
              </Box>
            ) : (
              <Box sx={{ width: "100%" }}>
                {/* Status display */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                    mt: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Статус:
                  </Typography>
                  <Chip
                    label={
                      eventData?.event?.status === "published"
                        ? "Опубликовано"
                        : "Черновик"
                    }
                    color={
                      eventData?.event?.status === "published"
                        ? "success"
                        : "default"
                    }
                    size="medium"
                  />
                  {eventData?.event?.status === "draft" && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PublishIcon />}
                      onClick={() =>
                        selectedEventId && handlePublish(selectedEventId)
                      }
                      disabled={isPublishing}
                    >
                      Опубликовать
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<EmailIcon />}
                    onClick={() => handleSendEventPreviewEmail()}
                    disabled={isSendingEventPreviewEmail}
                    title="Отправить тестовое письмо на ваш email"
                  />
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<NotificationsActiveIcon />}
                    onClick={() => handleSendEventPreviewPush()}
                    disabled={isSendingEventPreviewPush}
                    title="Отправить тестовое уведомление на ваше устройство"
                  />
                </Box>

                <Tabs
                  value={dialogTab}
                  onChange={(_, newValue: EventDialogTab) =>
                    setDialogTab(newValue)
                  }
                >
                  <Tab label="Основная информация" />
                  <Tab label="QR-коды" />
                  <Tab label="Контент" />
                  <Tab label="Цены" />
                  <Tab label="Участники" />
                </Tabs>

                {dialogTab === EVENT_DIALOG_TAB.MAIN ? (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      fullWidth
                      label="Название"
                      required
                      value={updateFormState.title || ""}
                      onChange={(e) =>
                        handleUpdateChange("title", e.target.value)
                      }
                    />

                    <TextField
                      fullWidth
                      label="Описание"
                      required
                      multiline
                      rows={4}
                      value={updateFormState.description || ""}
                      onChange={(e) =>
                        handleUpdateChange("description", e.target.value)
                      }
                    />

                    <FormControl fullWidth required>
                      <InputLabel>Категория</InputLabel>
                      <Select
                        value={updateFormState.categoryId || ""}
                        onChange={(e) =>
                          handleUpdateChange("categoryId", e.target.value)
                        }
                        label="Категория"
                      >
                        {categoriesData?.categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Клуб</InputLabel>
                      <Select
                        value={updateFormState.clubId ?? ""}
                        onChange={(e: SelectChangeEvent<number | "">) =>
                          handleUpdateChange(
                            "clubId",
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                          )
                        }
                        label="Клуб"
                      >
                        <MenuItem value="">Не выбран</MenuItem>
                        {clubsData?.clubs.map((club) => (
                          <MenuItem key={club.id} value={club.id}>
                            {club.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth required>
                      <InputLabel>Формат</InputLabel>
                      <Select
                        value={updateFormState.format || ""}
                        onChange={(e) =>
                          handleUpdateChange("format", e.target.value)
                        }
                        label="Формат"
                      >
                        <MenuItem value="online">Онлайн</MenuItem>
                        <MenuItem value="offline">Оффлайн</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth required>
                      <InputLabel>Город</InputLabel>
                      <Select
                        value={updateFormState.cityId || ""}
                        onChange={(e) =>
                          handleUpdateChange("cityId", e.target.value)
                        }
                        label="Город"
                      >
                        {citiesData?.cities.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Адрес"
                      required
                      value={updateFormState.address || ""}
                      onChange={(e) =>
                        handleUpdateChange("address", e.target.value)
                      }
                    />

                    <TextField
                      fullWidth
                      label="Ссылка на карту"
                      value={updateFormState.addressLocationUrl || ""}
                      onChange={(e) =>
                        handleUpdateChange("addressLocationUrl", e.target.value)
                      }
                    />
                    <TextField
                      fullWidth
                      label="URL трансляции"
                      value={updateFormState.broadcastUrl || ""}
                      onChange={(e) =>
                        handleUpdateChange("broadcastUrl", e.target.value)
                      }
                    />

                    <TextField
                      fullWidth
                      label="Максимальное число участников"
                      required
                      type="number"
                      inputProps={{ min: 1 }}
                      value={updateFormState.participantMax || ""}
                      onChange={(e) =>
                        handleUpdateChange(
                          "participantMax",
                          parseInt(e.target.value),
                        )
                      }
                    />

                    <Box sx={{ width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(
                              updateFormState.participantsFormed,
                            )}
                            onChange={(e) =>
                              handleUpdateChange(
                                "participantsFormed",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label="Состав участников сформирован"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Мероприятие останется в календаре, но записаться на него из приложения будет нельзя — пользователь увидит сообщение, что состав уже сформирован.
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      label="Дата и время начала регистрации"
                      type="datetime-local"
                      required
                      value={updateFormState.registrationStartAt || ""}
                      onChange={(e) =>
                        handleUpdateChange(
                          "registrationStartAt",
                          e.target.value,
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Дата и время окончания регистрации"
                      type="datetime-local"
                      required
                      value={updateFormState.registrationEndAt || ""}
                      onChange={(e) =>
                        handleUpdateChange("registrationEndAt", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Дата и время начала мероприятия"
                      type="datetime-local"
                      required
                      value={updateFormState.startAt || ""}
                      onChange={(e) =>
                        handleUpdateChange("startAt", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Дата и время окончания мероприятия"
                      type="datetime-local"
                      required
                      value={updateFormState.endAt || ""}
                      onChange={(e) =>
                        handleUpdateChange("endAt", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <FormControl fullWidth>
                      <InputLabel>Спикеры</InputLabel>
                      <Select
                        multiple
                        value={updateFormState.speakers || []}
                        onChange={(e) =>
                          handleUpdateChange(
                            "speakers",
                            e.target.value as unknown as number[],
                          )
                        }
                        input={<OutlinedInput label="Спикеры" />}
                      >
                        {speakersData?.speakers?.map((speaker) => (
                          <MenuItem key={speaker.id} value={speaker.id}>
                            {`${speaker.lastName} ${speaker.firstName} ${
                              speaker.patronymic || ""
                            }`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth required>
                      <InputLabel>Роли пользователей</InputLabel>
                      <Select
                        multiple
                        value={updateFormState.roles || []}
                        onChange={(e) => handleRolesChange(e, false)}
                        input={<OutlinedInput label="Роли пользователей" />}
                      >
                        {Object.values(UserRole).map((role) => (
                          <MenuItem key={role} value={role}>
                            {getRoleDisplayName(role)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      fullWidth
                      disabled={
                        updateFormState.roles?.length === 1 &&
                        updateFormState.roles[0] === UserRole.USER
                      }
                    >
                      <InputLabel>Доступен резидентам</InputLabel>
                      <Select
                        multiple
                        value={updateFormState.calibers || []}
                        onChange={(e) => handleCalibersChange(e, false)}
                        input={<OutlinedInput label="Доступен резидентам" />}
                      >
                        {Object.values(ResidentCaliber).map((resident) => (
                          <MenuItem key={resident} value={resident}>
                            {getResidentDisplayName(resident)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Thumbnail image upload */}
                    <Box sx={{ width: "100%", mt: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Миниатюра мероприятия
                      </Typography>
                      {selectedEvent?.thumbnailImageUrl &&
                        !thumbnailPreview && (
                          <Box sx={{ mb: 2, maxWidth: "300px" }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Текущее изображение:
                            </Typography>
                            <img
                              src={selectedEvent.thumbnailImageUrl}
                              alt="Текущая миниатюра"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "150px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleThumbnailChange}
                        ref={thumbnailInputRef}
                        id="thumbnail-upload-edit"
                      />
                      <label htmlFor="thumbnail-upload-edit">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ mb: 2 }}
                        >
                          {selectedEvent?.thumbnailImageUrl
                            ? "Изменить миниатюру"
                            : "Загрузить миниатюру"}
                        </Button>
                      </label>
                      <FileUploadInfo
                        type="image"
                        recommendedSize="400x300 пикселей"
                      />
                      {thumbnailPreview && (
                        <Box
                          sx={{
                            mt: 2,
                            position: "relative",
                            maxWidth: "300px",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Новое изображение:
                          </Typography>
                          <img
                            src={thumbnailPreview}
                            alt="Предпросмотр миниатюры"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "150px",
                              objectFit: "contain",
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={handleClearThumbnail}
                            sx={{
                              position: "absolute",
                              top: 20,
                              right: 0,
                              bgcolor: "rgba(255,255,255,0.7)",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    {/* Large image upload */}
                    <Box sx={{ width: "100%", mt: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Большое изображение мероприятия
                      </Typography>
                      {eventData?.event?.largeImageUrl &&
                        !largeImagePreview && (
                          <Box sx={{ mb: 2, maxWidth: "300px" }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Текущее изображение:
                            </Typography>
                            <img
                              src={eventData.event.largeImageUrl}
                              alt="Текущее большое изображение"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "150px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleLargeImageChange}
                        ref={largeImageInputRef}
                        id="large-image-upload-edit"
                      />
                      <label htmlFor="large-image-upload-edit">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ mb: 2 }}
                        >
                          {eventData?.event?.largeImageUrl
                            ? "Изменить большое изображение"
                            : "Загрузить большое изображение"}
                        </Button>
                      </label>
                      <FileUploadInfo
                        type="image"
                        recommendedSize="1920x1080 пикселей"
                      />
                      {largeImagePreview && (
                        <Box
                          sx={{
                            mt: 2,
                            position: "relative",
                            maxWidth: "300px",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Новое изображение:
                          </Typography>
                          <img
                            src={largeImagePreview}
                            alt="Предпросмотр большого изображения"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "150px",
                              objectFit: "contain",
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={handleClearLargeImage}
                            sx={{
                              position: "absolute",
                              top: 20,
                              right: 0,
                              bgcolor: "rgba(255,255,255,0.7)",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ) : dialogTab === EVENT_DIALOG_TAB.PRICES ? (
                  <Box sx={{ mt: 2 }}>
                    {isCreatingPrice || isUpdatingPrice || isDeletingPrice ? (
                      <LoadingIndicator />
                    ) : null}
                    <Typography variant="subtitle1" gutterBottom>
                      Цена действует с указанной даты и времени (в часовом поясе
                      города мероприятия). Можно добавить несколько цен —
                      например, повышение ближе к дате мероприятия.
                    </Typography>
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Добавить цену
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid sx={{ xs: 12, sm: 6, md: 3 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Тип цены</InputLabel>
                            <Select
                              value={
                                newPriceForm.typeId ??
                                EVENT_PRICE_TYPE.NON_RESIDENT
                              }
                              label="Тип цены"
                              onChange={(e) =>
                                setNewPriceForm((prev) => ({
                                  ...prev,
                                  typeId: Number(e.target.value) as 1 | 2,
                                }))
                              }
                            >
                              <MenuItem value={EVENT_PRICE_TYPE.NON_RESIDENT}>
                                {getEventPriceTypeDisplayName(
                                  EVENT_PRICE_TYPE.NON_RESIDENT,
                                )}
                              </MenuItem>
                              <MenuItem value={EVENT_PRICE_TYPE.RESIDENT}>
                                {getEventPriceTypeDisplayName(
                                  EVENT_PRICE_TYPE.RESIDENT,
                                )}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid sx={{ xs: 12, sm: 6, md: 3 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Валюта</InputLabel>
                            <Select
                              value={
                                newPriceForm.currencyId ?? CURRENCIES[0].id
                              }
                              label="Валюта"
                              onChange={(e) =>
                                setNewPriceForm((prev) => ({
                                  ...prev,
                                  currencyId: Number(e.target.value),
                                }))
                              }
                            >
                              {CURRENCIES.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.symbol} {c.code} — {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid sx={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Сумма"
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            value={newPriceForm.value ?? ""}
                            onChange={(e) =>
                              setNewPriceForm((prev) => ({
                                ...prev,
                                value:
                                  e.target.value === ""
                                    ? undefined
                                    : parseFloat(e.target.value) || undefined,
                              }))
                            }
                          />
                        </Grid>
                        <Grid sx={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Действует с"
                            type="datetime-local"
                            value={newPriceForm.startAt ?? ""}
                            onChange={(e) =>
                              setNewPriceForm((prev) => ({
                                ...prev,
                                startAt: e.target.value,
                              }))
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid sx={{ xs: 12, sm: 6, md: 2 }}>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreatePrice}
                            disabled={
                              !newPriceForm.startAt ||
                              newPriceForm.value == null ||
                              newPriceForm.value === undefined ||
                              newPriceForm.value < 0
                            }
                          >
                            Добавить
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                    {eventPricesData?.prices &&
                    eventPricesData.prices.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Тип</TableCell>
                              <TableCell>Валюта</TableCell>
                              <TableCell align="right">Сумма</TableCell>
                              <TableCell>Действует с</TableCell>
                              <TableCell align="right">Действия</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {eventPricesData.prices.map((price) => (
                              <TableRow key={price.id}>
                                {editingPriceId === price.id ? (
                                  <>
                                    <TableCell>
                                      <FormControl
                                        size="small"
                                        sx={{ minWidth: 160 }}
                                      >
                                        <Select
                                          value={
                                            editPriceForm.typeId ?? price.typeId
                                          }
                                          onChange={(e) =>
                                            setEditPriceForm((prev) => ({
                                              ...prev,
                                              typeId: Number(e.target.value) as
                                                | 1
                                                | 2,
                                            }))
                                          }
                                        >
                                          <MenuItem
                                            value={
                                              EVENT_PRICE_TYPE.NON_RESIDENT
                                            }
                                          >
                                            {getEventPriceTypeDisplayName(
                                              EVENT_PRICE_TYPE.NON_RESIDENT,
                                            )}
                                          </MenuItem>
                                          <MenuItem
                                            value={EVENT_PRICE_TYPE.RESIDENT}
                                          >
                                            {getEventPriceTypeDisplayName(
                                              EVENT_PRICE_TYPE.RESIDENT,
                                            )}
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      <FormControl
                                        size="small"
                                        sx={{ minWidth: 120 }}
                                      >
                                        <Select
                                          value={
                                            editPriceForm.currencyId ??
                                            (() => {
                                              const currency = CURRENCIES.find(
                                                (c) =>
                                                  c.symbol === price.price.unicode ||
                                                  c.code === price.price.unicode
                                              );
                                              return currency?.id ?? CURRENCIES[0].id;
                                            })()
                                          }
                                          onChange={(e) =>
                                            setEditPriceForm((prev) => ({
                                              ...prev,
                                              currencyId: Number(
                                                e.target.value,
                                              ),
                                            }))
                                          }
                                        >
                                          {CURRENCIES.map((c) => (
                                            <MenuItem key={c.id} value={c.id}>
                                              {c.symbol} {c.code}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell align="right">
                                      <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        sx={{ width: 100 }}
                                        value={
                                          editPriceForm.value ??
                                          price.price.value
                                        }
                                        onChange={(e) =>
                                          setEditPriceForm((prev) => ({
                                            ...prev,
                                            value:
                                              e.target.value === ""
                                                ? undefined
                                                : parseFloat(e.target.value) ||
                                                  undefined,
                                          }))
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        type="datetime-local"
                                        sx={{ minWidth: 200 }}
                                        value={editPriceForm.startAt ?? ""}
                                        onChange={(e) =>
                                          setEditPriceForm((prev) => ({
                                            ...prev,
                                            startAt: e.target.value,
                                          }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        justifyContent="flex-end"
                                      >
                                        <Button
                                          size="small"
                                          onClick={handleUpdatePrice}
                                        >
                                          Сохранить
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={handleCancelEditPrice}
                                        >
                                          Отмена
                                        </Button>
                                      </Stack>
                                    </TableCell>
                                  </>
                                ) : (
                                  <>
                                    <TableCell>
                                      {getEventPriceTypeDisplayName(
                                        price.typeId,
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {price.price.unicode} (
                                      {CURRENCIES.find(
                                        (c) =>
                                          c.symbol === price.price.unicode ||
                                          c.code === price.price.unicode
                                      )?.code ?? ""}
                                      )
                                    </TableCell>
                                    <TableCell align="right">
                                      {price.price.value}
                                    </TableCell>
                                    <TableCell>
                                      {formatDateInCityTimezone(
                                        price.startAt,
                                        cityName,
                                        {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        },
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        justifyContent="flex-end"
                                      >
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() =>
                                            handleStartEditPrice(price)
                                          }
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleDeletePrice(price.id)
                                          }
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">
                        Цены не добавлены. Добавьте цену выше.
                      </Typography>
                    )}
                  </Box>
                ) : dialogTab === EVENT_DIALOG_TAB.QR_CODES ? (
                  <Box sx={{ mt: 2 }}>
                    {isCreatingQRCode || isDeletingQRCode ? (
                      <LoadingIndicator />
                    ) : null}
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                      <DatePicker
                        label="Дата истечения QR-кода"
                        value={qrCodeExpiresAt}
                        onChange={(date) => setQrCodeExpiresAt(date)}
                        sx={{ mr: 2 }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleCreateQRCode}
                        disabled={!qrCodeExpiresAt}
                      >
                        Создать QR-код
                      </Button>
                    </Box>
                    {eventQRCodesData?.codes?.map((code) => (
                      <Box
                        key={code.code}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 2,
                          mb: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            QR-код: {code.code}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Создан:{" "}
                            {format(
                              parseISO(code.createdAt),
                              "dd.MM.yyyy HH:mm",
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Истекает:{" "}
                            {format(
                              parseISO(code.expiresAt),
                              "dd.MM.yyyy HH:mm",
                            )}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            Ссылка: {code.universalLink}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() =>
                              handleDownloadQRCode(
                                code.universalLink,
                                code.code,
                              )
                            }
                          >
                            Скачать
                          </Button>
                          <Button
                            color="error"
                            onClick={() => handleDeleteQRCode(code.code)}
                          >
                            Удалить
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                ) : dialogTab === EVENT_DIALOG_TAB.CONTENT ? (
                  <EventContentManagement eventId={selectedEventId || 0} />
                ) : dialogTab === EVENT_DIALOG_TAB.APPLICATIONS ? (
                  <EventApplicationsManagement
                    eventId={selectedEventId || 0}
                    registrationEndAt={eventData?.event?.registrationEndAt}
                  />
                ) : null}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUpdateDialog}>Отмена</Button>
            {dialogTab === EVENT_DIALOG_TAB.MAIN && (
              <Button
                onClick={handleUpdate}
                variant="contained"
                disabled={isUpdating || isEventLoading}
              >
                Обновить
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Диалог подтверждения удаления */}
        <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Подтверждение удаления</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Вы уверены, что хотите удалить это мероприятие?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Отмена</Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              Удалить
            </Button>
          </DialogActions>
        </Dialog>

        {/* Модальное окно статистики */}
        <EventStatsModal
          open={statsModalOpen}
          onClose={handleCloseStatsModal}
          eventId={selectedEventForStats?.id || 0}
          eventTitle={selectedEventForStats?.title || ""}
        />

        <Snackbar
          open={previewSnackbar.open}
          autoHideDuration={6000}
          onClose={() => setPreviewSnackbar((p) => ({ ...p, open: false }))}
          message={previewSnackbar.message}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          ContentProps={{ sx: { whiteSpace: "pre-line" } }}
        />
      </Container>
    </LocalizationProvider>
  );
};

export const EventsManagement = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderColor: "divider", pl: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Мероприятия" />
          <Tab label="Категории" />
          <Tab label="Спикеры" />
        </Tabs>
      </Box>
      <Box>
        {(() => {
          switch (activeTab) {
            case 0:
              return <EventsList />;
            case 1:
              return <EventCategoriesManagement />;
            case 2:
              return <EventSpeakersManagement />;
            default:
              return null;
          }
        })()}
      </Box>
    </Box>
  );
};
